// The account's own provider key, read and presented (v3 `orpheusApiKey.ts` +
// `orpheusKeyPresentation.ts`). Band credit numbers are admin-tunable and always read from
// `orpheus-settings`, never written here.
import type {
  OrpheusApiKeySummaryDto,
  OrpheusBandSetDto,
  OrpheusKeyError,
  OrpheusKeyErrorType,
  OrpheusKeyStatus,
  OrpheusProvider,
  OrpheusProviderMeta,
} from '../models/orpheusKey.types'

import type { Translate } from './orpheusSetupCardPresentation'

import { ApiError } from '@/shared/http'

const K = 'orpheus.apiKey'

export const ORPHEUS_KEY_PROVIDERS: Record<OrpheusProvider, OrpheusProviderMeta> = {
  anthropic: {
    id: 'anthropic',
    name: 'Anthropic',
    descriptionKey: `${K}.provider.anthropic.description`,
    monogram: 'A',
    monogramClass: 'bg-chip-orange-bg text-chip-orange-text',
    placeholder: 'sk-ant-api03-…',
    keyHintKey: `${K}.provider.anthropic.hint`,
    billingUrl: 'https://console.anthropic.com/settings/billing',
  },
  openai: {
    id: 'openai',
    name: 'OpenAI',
    descriptionKey: `${K}.provider.openai.description`,
    monogram: 'O',
    monogramClass: 'bg-primary text-primary-fg',
    placeholder: 'sk-proj-…',
    keyHintKey: `${K}.provider.openai.hint`,
    billingUrl: 'https://platform.openai.com/settings/organization/billing/overview',
  },
}

/** Shown with "Contact us" only — they open support and are never configured from the product. */
export const ORPHEUS_CONTACT_PROVIDERS: OrpheusProviderMeta[] = [
  {
    id: 'microsoft-copilot',
    name: 'Microsoft Copilot',
    descriptionKey: `${K}.provider.copilot.description`,
    monogram: 'C',
    monogramClass: 'bg-chip-blue-bg text-chip-blue-text',
    contactOnly: true,
  },
  {
    id: 'gemini',
    name: 'Gemini',
    descriptionKey: `${K}.provider.gemini.description`,
    monogram: 'G',
    monogramClass: 'bg-chip-blue-bg text-chip-blue-text',
    contactOnly: true,
  },
]

export function isOrpheusProvider(value: unknown): value is OrpheusProvider {
  return value === 'anthropic' || value === 'openai'
}

/** The provider's display name; `null` is the caller's to word ("The provider"). */
export function providerName(provider: string | null | undefined): string | null {
  if (!provider) return null
  return isOrpheusProvider(provider)
    ? ORPHEUS_KEY_PROVIDERS[provider].name
    : provider.charAt(0).toUpperCase() + provider.slice(1)
}

/** "1–3" from a band set. */
export function bandRange(bands: OrpheusBandSetDto | null | undefined): string {
  if (!bands) return ''
  const values = [bands.band1_credits, bands.band2_credits, bands.band3_credits].filter((value) =>
    Number.isFinite(value),
  )
  if (!values.length) return ''
  const min = Math.min(...values)
  const max = Math.max(...values)
  return min === max ? `${min}` : `${min}–${max}`
}

/** "3–29" from the `{min, max}` a chat key error carries. */
export function creditRangeLabel(range: { min: number; max: number } | null | undefined): string {
  if (!range || !Number.isFinite(range.min) || !Number.isFinite(range.max)) return ''
  return range.min === range.max ? `${range.min}` : `${range.min}–${range.max}`
}

const KNOWN_ERROR_TYPES: readonly OrpheusKeyErrorType[] = [
  'out_of_credit',
  'invalid_key',
  'permission_denied',
  'provider_disabled',
  'rate_limited',
  'provider_error',
  'unreachable',
  'unknown',
]

function envelope(error: ApiError): Record<string, unknown> | null {
  const body = error.payload
  const raw = body && typeof body === 'object' ? (body as Record<string, unknown>).error : null
  return raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : null
}

function finite(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null
}

/**
 * The typed `ORPHEUS_KEY_*` error a key save or a chat answer came back with, or null. These are
 * never CREDIT_DEPLETED: they must not open the Orphex credit lock.
 */
export function orpheusKeyError(cause: unknown): OrpheusKeyError | null {
  const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  if (!error.code?.startsWith('ORPHEUS_KEY_')) return null
  const rawDetails = envelope(error)?.details
  const details =
    rawDetails && typeof rawDetails === 'object' ? (rawDetails as Record<string, unknown>) : {}
  const type = KNOWN_ERROR_TYPES.includes(details.error_type as OrpheusKeyErrorType)
    ? (details.error_type as OrpheusKeyErrorType)
    : 'unknown'
  const range = details.orphex_credits as { min?: unknown; max?: unknown } | null | undefined
  const min = finite(range?.min)
  const max = finite(range?.max)
  const balance = details.balance as { remaining?: unknown } | null | undefined
  const role = details.caller_role
  return {
    code: error.code,
    type,
    provider: typeof details.provider === 'string' ? details.provider : null,
    providerMessage: typeof details.provider_message === 'string' ? details.provider_message : null,
    orphexCredits: min !== null && max !== null ? { min, max } : null,
    orphexCreditsAvailable: details.orphex_credits_available !== false,
    callerRole: role === 'owner' || role === 'admin' ? role : 'member',
    remaining: finite(balance?.remaining),
    replayable: true,
  }
}

/** Kinds where changing the key (or answering on Orphex credits) is the way forward. */
export function isKeyFixableError(type: OrpheusKeyErrorType): boolean {
  return ['out_of_credit', 'invalid_key', 'permission_denied', 'provider_disabled'].includes(type)
}

/**
 * What to say under the key field when saving a key failed: the provider's own message when it
 * sent one, our sentence when it couldn't be reached, else the envelope's message.
 */
export function keySaveErrorMessage(t: Translate, cause: unknown, provider: string): string {
  const error = ApiError.isApiError(cause) ? cause : ApiError.fromAxios(cause)
  const keyError = orpheusKeyError(error)
  const name = providerName(provider) ?? t(`${K}.theProvider`)
  if (keyError?.type === 'unreachable') return t(`${K}.saveError.unreachable`, { name })
  if (keyError?.type === 'provider_disabled') return t(`${K}.saveError.disabled`, { name })
  if (keyError?.providerMessage) return keyError.providerMessage
  const message = envelope(error)?.message
  return typeof message === 'string' && message ? message : t(`${K}.saveError.rejected`, { name })
}

/* ── Presentation ─────────────────────────────────────────────────────────── */

export interface KeyStatusBadge {
  labelKey: string
  badgeClass: string
}

const STATUS_BADGES: Record<OrpheusKeyStatus, KeyStatusBadge> = {
  working: { labelKey: `${K}.status.working`, badgeClass: 'bg-success-soft text-success' },
  out_of_credit: {
    labelKey: `${K}.status.outOfCredit`,
    badgeClass: 'bg-danger-soft text-danger',
  },
  rejected: { labelKey: `${K}.status.rejected`, badgeClass: 'bg-danger-soft text-danger' },
}

export function keyStatusBadge(status: string | null | undefined): KeyStatusBadge {
  return STATUS_BADGES[status as OrpheusKeyStatus] ?? STATUS_BADGES.working
}

/** `sk-ant-••••••••••••a1B2` — the stored key is never sent back, only its last four characters. */
export function maskedKey(apiKey: Pick<OrpheusApiKeySummaryDto, 'provider' | 'last4'>): string {
  const prefix = apiKey.provider === 'openai' ? 'sk-proj-' : 'sk-ant-'
  return `${prefix}${'•'.repeat(12)}${apiKey.last4 ?? ''}`
}

function validDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const date = new Date(iso)
  return Number.isNaN(date.getTime()) ? null : date
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  )
}

/** The words and the date format the key rows read in — `useFormat`'s, never a fixed locale. */
export interface KeyLabels {
  t: Translate
  formatDate: (value: string, options?: { dateStyle?: 'medium' }) => string
  formatDateTime: (value: string, options?: { dateStyle?: 'medium' }) => string
  formatTime: (value: string) => string
}

/** "14:32 today" / "12 Sep 2026, 14:32". */
export function whenLabel(
  labels: KeyLabels,
  iso: string | null | undefined,
  now = new Date(),
): string {
  const date = validDate(iso)
  if (!date || !iso) return ''
  if (isSameDay(date, now)) return labels.t(`${K}.when.today`, { time: labels.formatTime(iso) })
  return labels.formatDateTime(iso, { dateStyle: 'medium' })
}

/** "12 Sep 2026". */
export function shortDateLabel(labels: KeyLabels, iso: string | null | undefined): string {
  return iso && validDate(iso) ? labels.formatDate(iso, { dateStyle: 'medium' }) : ''
}

/** "just now" / "2 min ago" / "3 h ago" / "12 Sep 2026". */
export function relativeTimeLabel(
  labels: KeyLabels,
  iso: string | null | undefined,
  now = new Date(),
): string {
  const date = validDate(iso)
  if (!date) return ''
  const minutes = Math.floor((now.getTime() - date.getTime()) / 60_000)
  if (minutes < 1) return labels.t(`${K}.when.justNow`)
  if (minutes < 60) return labels.t(`${K}.when.minutesAgo`, { count: minutes })
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return labels.t(`${K}.when.hoursAgo`, { count: hours })
  return shortDateLabel(labels, iso)
}

/** The key row's "Last answer": the latest success, or when it started failing if later. */
export function lastAnswerLabel(
  labels: KeyLabels,
  apiKey: OrpheusApiKeySummaryDto,
  now = new Date(),
): string {
  const success = validDate(apiKey.last_success_at)?.getTime() ?? 0
  const failure = validDate(apiKey.last_error_at)?.getTime() ?? 0
  if (apiKey.status !== 'working' && failure && failure >= success) {
    return labels.t(`${K}.when.failedAt`, { when: whenLabel(labels, apiKey.last_error_at, now) })
  }
  return relativeTimeLabel(labels, apiKey.last_success_at, now) || '—'
}
